/**
 * Générateur — Thalès (configurations triangle/papillon, réciproque),
 * triangles semblables, lignes trigonométriques (vocabulaire et relations).
 * Attendu ID : 240 | Niveau : 3ème | Domaine : Géométrie plane (d17)
 *
 * Source doc prof (ESPACE ET GEOMETRIE 3e, séances D4S1-D4S4 + D3S1). Plan
 * auto-validé 2026-07-18 (TODO §6) : « rapports » (quelle égalité de rapports
 * Thalès donne-t-il, QCM), « reciproque » (rapports égaux ou pas → conclusion,
 * QCM justifié), « produits » (produits en croix), « semblables » (troisièmes
 * angles), « cotes » (hypoténuse/opposé/adjacent), « relation » (cos/sin/tan
 * = quel quotient), « rotation » (angle dans les triangles caractéristiques,
 * 360 − α). Le Thalès CALCULATOIRE est déjà couvert par l'attendu 220 —
 * ici on teste la JUSTIFICATION. Constructions interactives écartées.
 */

import { randomChoice, randInt, shuffle, tex, makeQcm, makeTexteTrou } from '../../../utils';

/** Rapport de longueurs en KaTeX : rap('AM', 'AB') → « \dfrac{AM}{AB} ». */
const rap = (num, den) => `\\dfrac{${num}}{${den}}`;

const ATTENDU_ID = 240;
const L_RAPPORTS = 'Écrire les rapports de Thalès';
const L_RECIPROQUE = 'Parallèles ou pas ? (réciproque de Thalès)';
const L_PRODUITS = 'Produits en croix';
const L_SEMBLABLES = 'Triangles semblables : les troisièmes angles';
const L_COTES = 'Hypoténuse, côté opposé, côté adjacent';
const L_RELATION = 'Cosinus, sinus, tangente : le bon quotient';
const L_ROTATION = 'Angles de rotation caractéristiques';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const fr = (x) => String(x).replace('.', ',');
// Décimal exact ×10 (arithmétique entière) affiché sans zéro final.
const fr10 = (dixiemes) => (dixiemes % 10 === 0 ? String(dixiemes / 10) : `${Math.trunc(dixiemes / 10)},${Math.abs(dixiemes % 10)}`);

// ═════════ « rapports » — QCM ═════════

const TRIPLES = [['A', 'B', 'C'], ['S', 'R', 'T'], ['O', 'K', 'L'], ['E', 'F', 'G']];
const PAIRES_MN = [['M', 'N'], ['P', 'R'], ['U', 'V'], ['I', 'J']];

const genRapportsQcm = () => {
    const [A, B, C] = randomChoice(TRIPLES);
    let [M, N] = randomChoice(PAIRES_MN);
    if ([A, B, C].includes(M) || [A, B, C].includes(N)) [M, N] = ['M', 'N'];
    const papillon = randInt(0, 1) === 1;
    const contexte = papillon
        ? `<p>Les droites (${M}${B}) et (${N}${C}) se coupent en ${A}, avec ${A} entre ${M} et ${B} `
        + `d'une part, entre ${N} et ${C} d'autre part (configuration PAPILLON). Les droites (${M}${N}) et (${B}${C}) sont parallèles.</p>`
        : `<p>Dans le triangle ${A}${B}${C}, ${M} est un point de [${A}${B}] et ${N} un point de [${A}${C}], `
        + `et les droites (${M}${N}) et (${B}${C}) sont parallèles.</p>`;
    const correct = tex(`${rap(`${A}${M}`, `${A}${B}`)} = ${rap(`${A}${N}`, `${A}${C}`)} = ${rap(`${M}${N}`, `${B}${C}`)}`);
    const distracteurs = [
        tex(`${rap(`${A}${M}`, `${M}${B}`)} = ${rap(`${A}${N}`, `${N}${C}`)} = ${rap(`${M}${N}`, `${B}${C}`)}`),
        tex(`${rap(`${A}${M}`, `${A}${B}`)} = ${rap(`${A}${C}`, `${A}${N}`)} = ${rap(`${M}${N}`, `${B}${C}`)}`),
        tex(`${rap(`${A}${M}`, `${A}${B}`)} = ${rap(`${A}${N}`, `${A}${C}`)} = ${rap(`${B}${C}`, `${M}${N}`)}`),
    ];
    const payload = makeQcm([correct, ...distracteurs], correct,
        contexte + '<p><b>Quelle égalité de rapports le théorème de Thalès donne-t-il ?</b></p>',
        opts(L_RAPPORTS));
    payload._meta.params = { A, B, C, M, N, papillon };
    return payload;
};

// ═════════ « reciproque » — QCM justifié ═════════

// Les trois choix portent le MÊME niveau de justification : « les rapports
// sont égaux / ne sont pas égaux ». La parenthèse qui n'existait que sur le
// « Non » le désignait sans qu'on ait à comparer quoi que ce soit.
const CHOIX_RECIP = [
    'Oui : les rapports sont égaux, la réciproque du théorème de Thalès s\'applique',
    'Non : les rapports ne sont pas égaux',
    'On ne peut pas savoir sans mesurer sur la figure',
];

const genReciproqueQcm = () => {
    const [O] = randomChoice(TRIPLES);
    let [M, N] = randomChoice(PAIRES_MN);
    let [V, S] = randomChoice(PAIRES_MN.filter(p => p[0] !== M));
    if (O === M || O === N) [M, N] = ['M', 'N'];
    if (O === V || O === S || V === M || S === N) [V, S] = ['V', 'S'];
    // Longueurs en dixièmes (entiers) : OM/ON vs OV/OS.
    const egaux = randInt(0, 1) === 1;
    const om = randInt(2, 6) * 10;
    const k = randomChoice([15, 20, 25, 30]);               // rapport ×10 → ON = OM·k/10 entier
    const on = (om * k) / 100 * 10;
    const ov = randInt(2, 6) * 10;
    let os = (ov * k) / 100 * 10;
    if (!egaux) os += randomChoice([-10, 10, 20]);
    if (os <= ov) os = ov + 30;
    const vraimentEgaux = om * os === on * ov;
    const correct = vraimentEgaux ? CHOIX_RECIP[0] : CHOIX_RECIP[1];
    const payload = makeQcm([...CHOIX_RECIP], correct,
        `<p>${O}, ${M}, ${N} sont alignés dans cet ordre, ainsi que ${O}, ${V}, ${S}. `
        + `${tex(`${O}${M} = ${fr10(om).replace(',', '{,}')}`)} cm ; `
        + `${tex(`${O}${N} = ${fr10(on).replace(',', '{,}')}`)} cm ; `
        + `${tex(`${O}${V} = ${fr10(ov).replace(',', '{,}')}`)} cm ; `
        + `${tex(`${O}${S} = ${fr10(os).replace(',', '{,}')}`)} cm.</p>`
        // « Compare OM/ON et OV/OS, par produits en croix » donnait les deux
        // rapports à former ET la technique de comparaison.
        + `<p><b>Les droites (${M}${V}) et (${N}${S}) sont-elles parallèles ?</b></p>`,
        opts(L_RECIPROQUE));
    payload._meta.params = { O, M, N, V, S, om, on, ov, os, vraimentEgaux };
    return payload;
};

// ═════════ « produits » — produits en croix ═════════

const genProduitsTrous = () => {
    // a/b et c/d en dixièmes ; produits ad et bc en centièmes exacts.
    const egaux = randInt(0, 1) === 1;
    const a = randInt(2, 9) * randomChoice([1, 10]);
    const b = randInt(11, 39);
    const m = randomChoice([2, 3]);
    const c = a * m;
    let d = b * m;
    if (!egaux) d += randomChoice([m, 2 * m, 3 * m]);
    const p1 = a * d;                                       // en centièmes
    const p2 = b * c;
    const aff = (cent) => {
        const neg = cent < 0 ? '-' : '';
        const v = Math.abs(cent);
        if (v % 100 === 0) return neg + String(v / 100);
        if (v % 10 === 0) return `${neg}${Math.trunc(v / 100)},${(v % 100) / 10}`;
        return `${neg}${Math.trunc(v / 100)},${String(v % 100).padStart(2, '0')}`;
    };
    // Les FACTEURS sont eux aussi à trouver : les paires étaient pré-écrites,
    // l'élève n'avait plus qu'à effectuer deux multiplications élémentaires
    // alors que le cœur du produit en croix est le CHOIX des facteurs.
    const d10 = (x) => fr10(x).replace(',', '{,}');
    // `\times` et non « × » littéral : KaTeX rend les deux pareil, mais le
    // caractère Unicode déclenche un avertissement « LaTeX-incompatible ».
    const phrase = '$\\text{numérateur de gauche} \\times \\text{dénominateur de droite : }'
        + ` {{${fr10(a)}}} \\times {{${fr10(d)}}} = {{${aff(p1)}}}$\n`
        + '$\\text{dénominateur de gauche} \\times \\text{numérateur de droite : }'
        + ` {{${fr10(b)}}} \\times {{${fr10(c)}}} = {{${aff(p2)}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Pour savoir si les quotients ${tex(rap(d10(a), d10(b)))} et ${tex(rap(d10(c), d10(d)))} `
        + 'sont égaux, on calcule les PRODUITS EN CROIX : s\'ils sont égaux, les quotients le sont aussi.</p>'
        + '<p><b>Écris les deux produits en croix, puis calcule-les.</b></p>',
        { ...opts(L_PRODUITS), mode: 'saisie' });
    payload._meta.params = { a, b, c, d, egaux: p1 === p2 };
    return payload;
};

// ═════════ « semblables » — troisièmes angles ═════════

const NOMS_TRIANGLES = [['MAC', 'RMC'], ['ABC', 'DEF'], ['KIT', 'LOU'], ['RST', 'UVW']];

const genSemblablesQcm = () => {
    const [t1, t2] = randomChoice(NOMS_TRIANGLES);
    const a1 = randInt(35, 80);
    const b1 = randInt(30, Math.min(75, 170 - a1));
    const c1 = 180 - a1 - b1;                               // 3e angle du triangle 1
    const semblables = randInt(0, 1) === 1;
    const a2 = a1;                                          // un angle commun
    let b2 = semblables ? c1 : c1 + randomChoice([-7, -5, 4, 6, 9]);
    if (b2 === b1 || b2 <= 5 || a2 + b2 >= 175) b2 = semblables ? c1 : c1 + 11;
    const vraiment = (b2 === b1 || b2 === c1);
    const correct = vraiment
        ? 'Oui : leurs trois angles sont deux à deux de même mesure'
        : 'Non : les troisièmes angles ne se correspondent pas';
    const payload = makeQcm([
        'Oui : leurs trois angles sont deux à deux de même mesure',
        'Non : les troisièmes angles ne se correspondent pas',
        'On ne peut pas le savoir sans connaître les longueurs des côtés',
    ], correct,
        `<p>Le triangle ${t1} a deux angles de ${a1}° et ${b1}°. Le triangle ${t2} a deux angles `
        + `de ${a2}° et ${b2}°.</p>`
        // « Calcule les TROISIÈMES angles : un angle commun ne suffit pas ! »
        // donnait la méthode et le piège.
        + '<p><b>Ces deux triangles sont-ils semblables ?</b></p>',
        opts(L_SEMBLABLES));
    payload._meta.params = { t1, t2, a1, b1, a2, b2, vraiment };
    return payload;
};

// ═════════ « cotes » — vocabulaire du triangle rectangle ═════════

const TRIPLES_RECT = [['M', 'N', 'O'], ['S', 'R', 'T'], ['W', 'X', 'Y'], ['H', 'E', 'S'], ['A', 'B', 'C'], ['I', 'J', 'K'], ['D', 'O', 'G'], ['L', 'U', 'Z']];

const genCotesQcm = () => {
    const [P, Q, R] = randomChoice(TRIPLES_RECT);           // rectangle en R ; angle étudié en P
    const question = randomChoice(['hypotenuse', 'oppose', 'adjacent']);
    // `\lbrack` : un crochet brut casse le parseur `[ligne|colonne]` (§4).
    const seg = (u, v) => tex(`\\lbrack ${u}${v}\\rbrack`);
    const hyp = seg(P, Q);
    const opp = seg(Q, R);
    const adj = seg(P, R);
    const angle = tex(`\\widehat{${R}${P}${Q}}`);           // sommet = lettre centrale = P
    const libQ = question === 'hypotenuse' ? 'l\'HYPOTÉNUSE'
        : question === 'oppose' ? `le côté OPPOSÉ à l'angle ${angle}`
            : `le côté ADJACENT à l'angle ${angle}`;
    const correct = question === 'hypotenuse' ? hyp : question === 'oppose' ? opp : adj;
    const payload = makeQcm([hyp, opp, adj], correct,
        // « le sommet de l'angle est P, la lettre du milieu » : la notation
        // \widehat{RPQ} montre l'angle, et la règle de lecture est au programme.
        `<p>Le triangle ${P}${Q}${R} est rectangle en ${R}.</p>`
        + `<p><b>Quel segment est ${libQ} ?</b></p>`,
        opts(L_COTES));
    payload._meta.params = { P, Q, R, question };
    return payload;
};

// ═════════ « relation » — cos/sin/tan = quel quotient ═════════

const genRelationQcm = () => {
    const [P, Q, R] = randomChoice(TRIPLES_RECT);           // rectangle en R ; angle en P
    const rapport = randomChoice(['cos', 'sin', 'tan']);
    const angle = `\\widehat{${R}${P}${Q}}`;
    const cos = tex(rap(`${P}${R}`, `${P}${Q}`));
    const sin = tex(rap(`${Q}${R}`, `${P}${Q}`));
    const tan = tex(rap(`${Q}${R}`, `${P}${R}`));
    const correct = rapport === 'cos' ? cos : rapport === 'sin' ? sin : tan;
    const payload = makeQcm([cos, sin, tan], correct,
        `<p>Le triangle ${P}${Q}${R} est rectangle en ${R}.</p>`
        + `<p><b>Que vaut ${tex(`\\${rapport}\\left(${angle}\\right)`)} ?</b></p>`,
        opts(L_RELATION));
    payload._meta.params = { P, Q, R, rapport };
    return payload;
};

// ═════════ « rotation » — angles caractéristiques ═════════

const genRotationTrous = () => {
    let beta; let alpha;
    for (let essai = 0; essai < 30; essai++) {
        beta = randInt(50, 75);                             // angles à la base de l'isocèle
        alpha = randomChoice([45, 75, 100, 110, 135, 150]); // rotation anti-horaire donnée
        if (180 - 2 * beta !== 360 - alpha) break;          // trous distincts (piège drag/valeurs)
    }
    const sommet = randomChoice(['C', 'O', 'S']);
    const phrase = `$\\text{a. angle de la rotation : } {{${180 - 2 * beta}}}°$\n`
        + `$\\text{b. rotation de } {{${360 - alpha}}}° \\text{ dans le sens horaire}$`;
    const payload = makeTexteTrou(phrase,
        // Les deux rappels (180° dans un triangle, 360° pour un tour) sont des
        // connaissances de cours mobilisées ici : les rappeler à l'endroit exact
        // où il faut s'en servir revenait à donner le calcul.
        `<p>a. Un triangle est isocèle de sommet principal ${sommet}, avec deux angles à la base de ${beta}°. `
        + `Quel est l'angle de la rotation de centre ${sommet} qui envoie un sommet de la base sur l'autre ?</p>`
        + `<p>b. Une rotation de ${alpha}° dans le sens anti-horaire déplace les points exactement comme `
        + 'une rotation dans le sens HORAIRE d\'un autre angle. Lequel ?</p>'
        + '<p><b>Complète.</b></p>',
        { ...opts(L_ROTATION), mode: 'saisie' });
    payload._meta.params = { beta, alpha, sommet };
    return payload;
};

const GENERATORS = {
    rapports_qcm_unique: genRapportsQcm,
    reciproque_qcm_unique: genReciproqueQcm,
    produits_texte_trou_saisie: genProduitsTrous,
    semblables_qcm_unique: genSemblablesQcm,
    cotes_qcm_unique: genCotesQcm,
    relation_qcm_unique: genRelationQcm,
    rotation_texte_trou_saisie: genRotationTrous,
};
const FORMAT_LABELS = { qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'rapports', label: L_RAPPORTS, formatsList: buildFormats('rapports') },
    { id: 'reciproque', label: L_RECIPROQUE, formatsList: buildFormats('reciproque') },
    { id: 'produits', label: L_PRODUITS, formatsList: buildFormats('produits') },
    { id: 'semblables', label: L_SEMBLABLES, formatsList: buildFormats('semblables') },
    { id: 'cotes', label: L_COTES, formatsList: buildFormats('cotes') },
    { id: 'relation', label: L_RELATION, formatsList: buildFormats('relation') },
    { id: 'rotation', label: L_ROTATION, formatsList: buildFormats('rotation') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
